import React from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Layout from './components/layout/Layout';
import Login from './components/auth/Login';
import SuperAdminLogin from './components/auth/SuperAdminLogin';

import Dashboard from './components/dashboard/Dashboard';
import Cities from './components/cities/Cities';
import Events from './components/events/Events';
import Promotions from './components/promotions/Promotions';
import Contacts from './components/contacts/Contacts';
import Districts from './components/districts/Districts';
import Categories from './components/categories/Categories';
import EventTypes from './components/events/EventType';
import ServiceSubCategories from './components/categories/ServiceSubCategories';
import Users from './components/users/Users';
import Documents from './components/documents/Documents';
import DrivingLicence from './components/driving-licence/DrivingLicence';
import CityRegistration from './components/cityRegistration/CityRegistration';
import Recommendations from './components/recommendations/Recommendations';
import Accommodation from './components/accommodation/Accommodation';
import AccommodationFacilities from './components/accommodation/AccommodationFacilities';
import AccommodationTypes from './components/accommodation/AccommodationTypes';
import CustomerSupport from './components/customerSupport/CustomerSupport';
import InsuranceTypeDetailSummarys from './components/insurance/InsuranceTypeDetailSummarys';
import JobPortals from './components/jobPortals/JobPortals';
import LanguageSupport from './components/languageSupport/languagesupport.js';
import ContactInformationPage from './components/contact-Information/contactinformation.js';
import ContactInformationDetailsPage from './components/contactInformation-detail/contactinformationdetails.js';
import PromotionType from './components/promotiontype/promotiontype';
import DocumentsVerify from './components/documentsverification/documentsverify';
import NotificationsPage from './components/notifications/Notifications';
import LegalPages from './components/legal/LegalPages';
import ClaimProvider from './components/providerclaim/ClaimProvider';
import UserReports from './components/userReports/UserReports';
import RatingModulesSettings from './components/ratingModules/RatingModulesSettings';
import ProviderApplications from './components/providerApplications/ProviderApplications';
import ProviderNotifications from './components/notifications/ProviderNotifications';
import UserActivity from './components/userActivity/UserActivity';
import ReferralLink from './components/referralLink/ReferralLink';
import ContactGoogleRating from './components/contactGoogleRating/ContactGoogleRating';
import ContactInquiries from './components/contactInquiries/ContactInquiries';


import {
  ADMIN_IDLE_TIMEOUT_MS,
  expireAdminSessionIfIdle,
  getAdminLastActivityAt,
  getAdminToken,
  getAdminUser,
  markAdminActivity,
  clearAdminSession,
  clearAllAppStorageAndCache,
} from './utils/authSessionStorage';
import {
  getDefaultRouteForRole,
  isPathAllowedForRole,
} from './utils/roleUtils';

function ProtectedRoute({ children, isAuthenticated }) {
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  const adminUser = getAdminUser();
  if (!isPathAllowedForRole(adminUser?.role, location.pathname)) {
    const defaultRoute = getDefaultRouteForRole(adminUser?.role);
    return <Navigate to={defaultRoute} replace />;
  }
  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node,
  isAuthenticated: PropTypes.bool.isRequired,
};

function AuthRoute({ children, isAuthenticated }) {
  if (isAuthenticated) {
    const adminUser = getAdminUser();
    const defaultRoute = getDefaultRouteForRole(adminUser?.role);
    return <Navigate to={defaultRoute} replace />;
  }
  return children;
}

AuthRoute.propTypes = {
  children: PropTypes.node,
  isAuthenticated: PropTypes.bool.isRequired,
};

function DefaultRedirect() {
  const adminUser = getAdminUser();
  const defaultRoute = getDefaultRouteForRole(adminUser?.role);
  return <Navigate to={defaultRoute} replace />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => !!getAdminToken());
  console.log('Auth state:', isAuthenticated);

  // Clear storage and cache when not authenticated (e.g. after logout or tab close session)
  React.useEffect(() => {
    if (!isAuthenticated) {
      clearAllAppStorageAndCache();
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let logoutTimerId;

    const logoutForInactivity = () => {
      clearAdminSession();
      setIsAuthenticated(false);
      window.location.href = '/login';
    };

    const scheduleIdleLogout = () => {
      window.clearTimeout(logoutTimerId);
      const lastActivityAt = getAdminLastActivityAt() || Date.now();
      const remainingMs = Math.max(0, ADMIN_IDLE_TIMEOUT_MS - (Date.now() - lastActivityAt));
      logoutTimerId = window.setTimeout(logoutForInactivity, remainingMs);
    };

    const handleScreenClick = () => {
      if (expireAdminSessionIfIdle()) {
        logoutForInactivity();
        return;
      }
      markAdminActivity();
      scheduleIdleLogout();
    };

    const checkIdleExpiry = () => {
      if (expireAdminSessionIfIdle()) {
        logoutForInactivity();
        return;
      }
      scheduleIdleLogout();
    };

    scheduleIdleLogout();
    document.addEventListener('click', handleScreenClick, true);
    window.addEventListener('focus', checkIdleExpiry);
    document.addEventListener('visibilitychange', checkIdleExpiry);

    return () => {
      window.clearTimeout(logoutTimerId);
      document.removeEventListener('click', handleScreenClick, true);
      window.removeEventListener('focus', checkIdleExpiry);
      document.removeEventListener('visibilitychange', checkIdleExpiry);
    };
  }, [isAuthenticated]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BrowserRouter>
        <Routes>
          <Route path="/super-admin-login" element={
            <AuthRoute isAuthenticated={isAuthenticated}>
              <SuperAdminLogin />
            </AuthRoute>
          } />
          <Route path="/login" element={
            <AuthRoute isAuthenticated={isAuthenticated}>
              <Login />
            </AuthRoute>
          } />
          <Route element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout />
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/cities" element={<Cities />} />
            <Route path="/events" element={<Events />} />
            <Route path="/promotions" element={<Promotions />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/districts" element={<Districts />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/event-types" element={<EventTypes />} />
            <Route path="/promotion-types" element={<PromotionType />} />
            <Route path="/service-sub-categories" element={<ServiceSubCategories />} />
            <Route path="/users" element={<Users />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/driving-licence" element={<DrivingLicence />} />
            <Route path="/city-registrations" element={<CityRegistration />} />
            <Route path="/recommendations" element={<Recommendations />} />
            <Route path="/accommodation" element={<Accommodation />} />
            <Route path="/accommodation-facilities" element={<AccommodationFacilities />} />
            <Route path="/accommodation-types" element={<AccommodationTypes />} />
            <Route path="/customer-support" element={<CustomerSupport />} />
            <Route path="/insurance-summaries" element={<InsuranceTypeDetailSummarys />} />
            <Route path="/job-portals" element={<JobPortals />} />
            <Route path="/language-support" element={<LanguageSupport />} />
            <Route path="/documents-verification" element={<DocumentsVerify />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/legal" element={<LegalPages />} />
            <Route path="/provider-claims" element={<ClaimProvider />} />
            <Route path="/provider-applications" element={<ProviderApplications />} />
            <Route path="/provider-custom-notifications" element={<ProviderNotifications />} />
            <Route path="/user-reports" element={<UserReports />} />
            <Route path="/user-activity" element={<UserActivity />} />
            <Route path="/rating-modules" element={<RatingModulesSettings />} />
            <Route path="/referral-links" element={<ReferralLink />} />
            <Route path="/contact-google-rating" element={<ContactGoogleRating />} />
            <Route path="/contact-inquiries" element={<ContactInquiries />} />


            <Route path="/" element={<DefaultRedirect />} />
            <Route path="*" element={<DefaultRedirect />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </LocalizationProvider>
  );
}

export default App;
